import type {PersonName, Room} from '@/services/graphqlHooks';
import {localeCompareSort} from '@/utils/localeCompareSort';

export const NO_ROOM_KEY = 'no-room';

export type EvacuationStatus = {
  confirmedIds: Record<string, boolean>;
  absentIds: Record<string, boolean>;
};

type Person = {id: string; name: Pick<PersonName, 'firstName' | 'lastName'>};

type EvacuationRoom = Pick<Room, 'id' | 'roomNumber' | 'building'>;

export const formatPersonName = (
  name: Pick<PersonName, 'firstName' | 'lastName'>
) => `${name.firstName} ${name.lastName}`;

export const formatRoomLabel = (room: Pick<Room, 'roomNumber' | 'building'>) =>
  room.building ? `${room.roomNumber} (${room.building})` : room.roomNumber;

export const sortByPersonName = <T extends Person>(people: T[]): T[] =>
  [...people].sort((a, b) =>
    localeCompareSort(formatPersonName(a.name), formatPersonName(b.name))
  );

export const matchesSearch = (
  search: string,
  ...values: (string | undefined)[]
) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return values.some(value => value?.toLowerCase().includes(normalizedSearch));
};

export const groupByRoom = <T extends {roomId?: string | null}>(
  items: T[]
): Record<string, T[]> =>
  items.reduce<Record<string, T[]>>((groups, item) => {
    const key = item.roomId || NO_ROOM_KEY;

    return {...groups, [key]: [...(groups[key] ?? []), item]};
  }, {});

export const sortByEvacuationStatus = <T extends {id: string}>(
  items: T[],
  {confirmedIds, absentIds}: EvacuationStatus
): T[] => {
  const rank = ({id}: T) => {
    if (absentIds[id]) return 2;
    if (confirmedIds[id]) return 1;

    return 0;
  };

  return [...items].sort((a, b) => rank(a) - rank(b));
};

export const countEvacuated = <T extends {id: string}>(
  people: T[],
  {confirmedIds, absentIds}: EvacuationStatus
) => {
  const present = people.filter(person => !absentIds[person.id]);

  return {
    confirmedCount: present.filter(person => confirmedIds[person.id]).length,
    totalCount: present.length,
  };
};

export const getEvacuationCounts = (
  residents: {id: string}[],
  staff: {id: string}[],
  status: EvacuationStatus
) => {
  const residentCounts = countEvacuated(residents, status);
  const staffCounts = countEvacuated(staff, status);
  const confirmedTotal =
    residentCounts.confirmedCount + staffCounts.confirmedCount;
  const totalPeople = residentCounts.totalCount + staffCounts.totalCount;

  return {
    confirmedResidents: residentCounts.confirmedCount,
    confirmedStaff: staffCounts.confirmedCount,
    presentResidents: residentCounts.totalCount,
    presentStaff: staffCounts.totalCount,
    confirmedTotal,
    totalPeople,
    notOnSite: residents.length + staff.length - totalPeople,
    isEveryoneConfirmed: totalPeople > 0 && confirmedTotal === totalPeople,
  };
};

export const buildRoomSections = <
  T extends {id: string; roomId?: string | null},
>(
  residents: T[],
  rooms: EvacuationRoom[],
  noRoomTitle: string,
  status: EvacuationStatus
) => {
  const residentsByRoom = groupByRoom(residents);

  return [
    ...rooms
      .filter(room => residentsByRoom[room.id])
      .map(room => ({
        key: room.id,
        title: formatRoomLabel(room),
        residents: residentsByRoom[room.id],
      })),
    ...(residentsByRoom[NO_ROOM_KEY]
      ? [
          {
            key: NO_ROOM_KEY,
            title: noRoomTitle,
            residents: residentsByRoom[NO_ROOM_KEY],
          },
        ]
      : []),
  ].map(section => ({
    ...section,
    ...countEvacuated(section.residents, status),
  }));
};
